import { quoteOfferRoundFragment } from '~/api/requestForQuote';
import { useFragment as unmaskOfferRounds, type FragmentType } from '~/graphql';
import {
  QuoteRoundParty,
  type QuoteOfferRoundFragment,
} from '~/graphql/graphql';
import { MIN_PIECES_PER_BUNDLE } from '~/utils/bundleBounds';
import type { Price } from '~/utils/money';
import { isClosedRequestForQuoteStatus } from '~/utils/requestForQuoteClosed';
import { isQuoteAwardable } from './quoteAwarding';
import {
  isAwaitingSellerResponse,
  type NegotiableQuote,
} from './quoteNegotiation';
import type { Quote, QuoteItem } from './splitAwardGridTypes';

type OfferedQuote = NegotiableQuote & {
  rounds: ReadonlyArray<FragmentType<typeof quoteOfferRoundFragment>>;
};

type OrderableItem = {
  quantityInPieces: number;
  piecesPerBundle?: number | null;
  offeredPickupPrice?: Price | null;
  offeredDeliveryPrice?: Price | null;
  order?: { id: string } | null;
  platformProductsLength: {
    id: string;
    platformProduct: { piecesPerBundle: { value: number } };
  };
};

type OrderableLine = {
  order?: { id: string } | null;
  platformProductsLength: { id: string };
};

export type SellerOfferOrderBlock =
  'no_seller_offer' | 'tally_offer_lines_missing';

type TallyPricedQuote = OfferedQuote & {
  offeredPickupPriceForTally?: Price | null;
  offeredDeliveryPriceForTally?: Price | null;
};

export function isOrderedOnSellerOffer(quote: NegotiableQuote): boolean {
  return (
    !isAwaitingSellerResponse(quote) &&
    quote.negotiationAwaitingParty === QuoteRoundParty.Seller
  );
}

function latestSellerOffer(
  quote: OfferedQuote
): QuoteOfferRoundFragment | null {
  if (!isOrderedOnSellerOffer(quote)) {
    return null;
  }
  const sellerOffers = unmaskOfferRounds(quoteOfferRoundFragment, [
    ...quote.rounds,
  ]).filter((round) => round.party === QuoteRoundParty.Seller);
  return sellerOffers.reduce<QuoteOfferRoundFragment | null>(
    (latest, round) =>
      latest == null || round.roundNumber > latest.roundNumber ? round : latest,
    null
  );
}

function hasTallyPrice(offer: QuoteOfferRoundFragment): boolean {
  return (
    offer.pickupPriceForTally != null || offer.deliveryPriceForTally != null
  );
}

// A tally price covers the whole tally, so an order must take every line the seller offered it for.
function coversEveryOfferedLine(
  offer: QuoteOfferRoundFragment,
  lines: ReadonlyArray<OrderableLine>
): boolean {
  const orderableLengthIds = new Set(
    lines
      .filter((line) => line.order == null)
      .map((line) => line.platformProductsLength.id)
  );
  return offer.items.every((offeredItem) =>
    orderableLengthIds.has(offeredItem.platformProductsLength.id)
  );
}

export function sellerOfferOrderBlock(
  quote: OfferedQuote & { items: ReadonlyArray<OrderableLine> }
): SellerOfferOrderBlock | null {
  if (!isOrderedOnSellerOffer(quote)) {
    return null;
  }
  const offer = latestSellerOffer(quote);
  if (offer == null) {
    return 'no_seller_offer';
  }
  return hasTallyPrice(offer) && !coversEveryOfferedLine(offer, quote.items)
    ? 'tally_offer_lines_missing'
    : null;
}

export function isOfferSelectable(
  quote: Quote,
  item: QuoteItem,
  isSelectionDisabled: boolean
): boolean {
  return (
    !isSelectionDisabled &&
    item.order == null &&
    isQuoteAwardable(quote.status) &&
    !isClosedRequestForQuoteStatus(quote.requestForQuote.status) &&
    sellerOfferOrderBlock(quote) == null
  );
}

function piecesPerBundleOnOffer(
  item: OrderableItem,
  offeredPiecesPerBundle: number | null | undefined
): number | null {
  const ownPiecesPerBundle = item.piecesPerBundle ?? null;
  const productPiecesPerBundle =
    item.platformProductsLength.platformProduct.piecesPerBundle.value;
  if (offeredPiecesPerBundle == null) {
    return productPiecesPerBundle < MIN_PIECES_PER_BUNDLE
      ? null
      : ownPiecesPerBundle;
  }
  if (
    offeredPiecesPerBundle === (ownPiecesPerBundle ?? productPiecesPerBundle)
  ) {
    return ownPiecesPerBundle;
  }
  return offeredPiecesPerBundle === productPiecesPerBundle
    ? null
    : offeredPiecesPerBundle;
}

export function itemsOnSellerOffer<Item extends OrderableItem>(
  quote: OfferedQuote & { items: ReadonlyArray<Item> }
): Item[] {
  const offer = latestSellerOffer(quote);
  if (offer == null) {
    return [...quote.items];
  }
  const offeredByLengthId = new Map(
    offer.items.map((offeredItem) => [
      offeredItem.platformProductsLength.id,
      offeredItem,
    ])
  );
  return quote.items.flatMap((item) => {
    if (item.order != null) {
      return [item];
    }
    const offeredItem = offeredByLengthId.get(item.platformProductsLength.id);
    return offeredItem == null
      ? []
      : [
          {
            ...item,
            quantityInPieces: offeredItem.quantityInPieces,
            piecesPerBundle: piecesPerBundleOnOffer(
              item,
              offeredItem.piecesPerBundle
            ),
            offeredPickupPrice: offeredItem.pickupPrice ?? null,
            offeredDeliveryPrice: offeredItem.deliveryPrice ?? null,
          },
        ];
  });
}

export function quoteOnSellerOffer<
  Quote extends TallyPricedQuote & { items: ReadonlyArray<OrderableItem> },
>(quote: Quote): Quote {
  const offer = latestSellerOffer(quote);
  if (offer == null) {
    return quote;
  }
  return {
    ...quote,
    items: itemsOnSellerOffer(quote),
    offeredPickupPriceForTally: offer.pickupPriceForTally ?? null,
    offeredDeliveryPriceForTally: offer.deliveryPriceForTally ?? null,
  };
}
